import Alpine from "alpinejs";
import intersect from "@alpinejs/intersect";
import collapse from "@alpinejs/collapse";
import lazySizes from "lazysizes";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Swup from "swup";
import EmblaCarousel from "embla-carousel";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import Fade from "embla-carousel-fade";
import Autoplay from "embla-carousel-autoplay";
import { scaleControl } from "./archiveLayout.js";

gsap.registerPlugin(ScrollTrigger);

// Alpine.js Setup 🏔️
window.Alpine = Alpine;
Alpine.plugin(intersect);
Alpine.plugin(collapse);
Alpine.start();

window._logging = false; // turn on/off logging
const log = (...args) => window._logging && console.log(...args);

// Global Variables 🌍
const nav = document.querySelector(".nav");
const body = document.body;
const isMobile = window.matchMedia(
  "only screen and (max-width: 768px)",
).matches;

// Utility Functions 🛠️
const easeInOutQuad = (t) =>
  t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
const easeInCubic = (t) => t * t * t;
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

// Scroll Animations 🚀
const initAnimations = () => {
  log("🎬 Animations initialized");
};

// ==========================================================================
// Utility Functions
// ==========================================================================

const setActiveNav = () => {
  log("🔗 Setting active navigation");

  const nav = document.querySelector("#nav");
  if (!nav) return;

  const currentSlug = window.location.pathname.replace(/\/$/, "") || "/";

  nav.querySelectorAll(".nav__link a").forEach((link) => {
    const href = link.getAttribute("href");
    if (!href) return;

    const linkSlug =
      new URL(href, window.location.origin).pathname.replace(/\/$/, "") || "/";

    link.classList.toggle("active", linkSlug === currentSlug);
  });
};

// Check if on Home Page 🏠
const checkHomePage = () => {
  log("🏠 Checking home page");
  body.classList.toggle(
    "body--home",
    window.location.pathname === "/" ||
      window.location.href === window.location.origin + "/",
  );
};

// Remove <br> elements on mobile 📱
const removeBr = (el) => {
  if (window.innerWidth <= 768) {
    log("🗑️ Removing <br> elements from", el);
    const item = document.querySelector(el);
    if (item)
      item.innerHTML = item.innerHTML.replace(/<br\s*\/?>(\s*)?/gi, " ");
  }
};

// ==========================================================================
// Projects Fade Up Animation
// ==========================================================================

function fadeUp() {
  gsap.registerPlugin(ScrollTrigger);

  const els = document.querySelectorAll(".f-up");

  gsap.set(els, {
    opacity: 0,
    y: 30,
  });

  ScrollTrigger.batch(els, {
    start: "top 90%",
    onEnter: (batch) => {
      gsap.to(batch, {
        opacity: 1,
        y: 0,
        stagger: 0.15,
        duration: 0.6,
        ease: "power2.out",
        onComplete: function () {
          this.targets().forEach((el) => {
            el.classList.remove("f-up");
            el.classList.add("faded");
          });
        },
      });
    },
    once: true,
  });

  const visibleEls = [];
  els.forEach((el) => {
    const r = el.getBoundingClientRect();
    if (r.top < window.innerHeight && r.bottom > 0) {
      visibleEls.push(el);
    }
  });

  if (visibleEls.length > 0) {
    gsap.to(visibleEls, {
      opacity: 1,
      y: 0,
      stagger: 0.15,
      duration: 0.6,
      ease: "power2.out",
      onComplete: function () {
        this.targets().forEach((el) => {
          el.classList.remove("f-up");
          el.classList.add("faded");
        });
      },
    });
  }

  let sTimeout;
  const scrollHandler = () => {
    clearTimeout(sTimeout);
    sTimeout = setTimeout(() => {
      const missedEls = [];
      document.querySelectorAll(".f-up").forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) {
          missedEls.push(el);
        }
      });

      if (missedEls.length > 0) {
        gsap.to(missedEls, {
          opacity: 1,
          y: 0,
          stagger: 0.15,
          duration: 0.4,
          ease: "power2.out",
          onComplete: function () {
            this.targets().forEach((el) => {
              el.classList.remove("f-up");
              el.classList.add("faded");
            });
          },
        });
      }
    }, 150);
  };

  window.addEventListener("scroll", scrollHandler);

  return () => {
    window.removeEventListener("scroll", scrollHandler);
    ScrollTrigger.getAll().forEach((st) => st.kill());
  };
}

// ==========================================================================
// Load More for Carousel
// ==========================================================================

let emblaMain = null;
let emblaOverlay = null;
let emblaProcesses = [];

const embla = (selector, plugins = [], options = { loop: true }) => {
  if (typeof EmblaCarousel !== "function") {
    log("❌ EmblaCarousel is not defined");
    return [];
  }

  let nodes = [];
  if (typeof selector === "string") {
    nodes = document.querySelectorAll(selector);
  } else if (selector instanceof Element) {
    nodes = [selector];
  } else if (selector instanceof NodeList || Array.isArray(selector)) {
    nodes = selector;
  } else {
    log("❌ Invalid selector passed to embla:", selector);
    return [];
  }

  const instances = [];

  nodes.forEach((node) => {
    const isAutoWidth = node.classList.contains("embla--aw");
    const isSimpleParent = node.closest(".carousel--simple");
    const config = { ...options };

    if (isAutoWidth && !isSimpleParent) {
      config.dragFree = false;
    }

    if (node.embla) node.embla.destroy();

    const emblaApi = EmblaCarousel(node, config, plugins);
    if (!emblaApi) return;

    node.embla = emblaApi;
    instances.push(emblaApi);

    const nextButton = node.parentElement?.querySelector(".embla__next");
    const prevButton = node.parentElement?.querySelector(".embla__prev");

    if (nextButton)
      nextButton.addEventListener("click", () => emblaApi.scrollNext());
    if (prevButton)
      prevButton.addEventListener("click", () => emblaApi.scrollPrev());
  });

  return instances;
};

// Add keyboard and Magic Mouse navigation function
const addKeyboardNavigation = (emblaApi, carouselType = "default") => {
  if (!emblaApi || typeof emblaApi.scrollNext !== "function") return;

  let scrollAccumulator = 0;
  let scrollTimeout = null;
  let isScrolling = false;
  const SCROLL_THRESHOLD = 30; // Lower threshold for initial detection
  const SCROLL_COOLDOWN = 300; // Prevent multiple triggers during one swipe gesture

  const shouldHandleNavigation = () => {
    // Don't handle if typing in inputs, textareas, or if scale control has focus
    if (
      document.activeElement &&
      (document.activeElement.tagName === "INPUT" ||
        document.activeElement.tagName === "TEXTAREA" ||
        document.activeElement.id === "scale-range" ||
        document.activeElement.closest(".scale-control") ||
        document.activeElement.getAttribute("role") === "slider" ||
        document.activeElement.matches('input[type="range"]'))
    ) {
      return false;
    }

    // Check if overlay is active - only overlay carousel should respond when overlay is open
    const overlay = document.getElementById("overlay");
    const isOverlayActive = overlay && overlay.classList.contains("active");

    if (isOverlayActive && carouselType !== "overlay") {
      return false;
    }

    if (!isOverlayActive && carouselType === "overlay") {
      return false;
    }

    return true;
  };

  const handleKeyDown = (e) => {
    if (!shouldHandleNavigation()) return;

    switch (e.key) {
      case "ArrowLeft":
        e.preventDefault();
        emblaApi.scrollPrev();
        log(`⬅️ ${carouselType} carousel: Previous slide (keyboard)`);
        break;
      case "ArrowRight":
        e.preventDefault();
        emblaApi.scrollNext();
        log(`➡️ ${carouselType} carousel: Next slide (keyboard)`);
        break;
    }
  };

  const handleWheel = (e) => {
    if (!shouldHandleNavigation() || isScrolling) return;

    // Only handle horizontal scrolling (Magic Mouse swipes)
    if (
      Math.abs(e.deltaX) > Math.abs(e.deltaY) &&
      Math.abs(e.deltaX) > SCROLL_THRESHOLD
    ) {
      e.preventDefault();

      // Set scrolling flag to prevent multiple triggers
      isScrolling = true;

      if (e.deltaX > 0) {
        // Scrolling right (next slide)
        emblaApi.scrollNext();
        log(`➡️ ${carouselType} carousel: Next slide (Magic Mouse)`);
      } else {
        // Scrolling left (previous slide)
        emblaApi.scrollPrev();
        log(`⬅️ ${carouselType} carousel: Previous slide (Magic Mouse)`);
      }

      // Reset scrolling flag after cooldown
      setTimeout(() => {
        isScrolling = false;
      }, SCROLL_COOLDOWN);
    }
  };

  // Add event listeners
  document.addEventListener("keydown", handleKeyDown);
  document.addEventListener("wheel", handleWheel, { passive: false });

  // Store the handlers so we can remove them later
  emblaApi._keyboardHandler = handleKeyDown;
  emblaApi._wheelHandler = handleWheel;

  // Clean up when carousel is destroyed
  const originalDestroy = emblaApi.destroy;
  emblaApi.destroy = function () {
    document.removeEventListener("keydown", handleKeyDown);
    document.removeEventListener("wheel", handleWheel);
    return originalDestroy.call(this);
  };
};

// Intelligent carousel image preloading
const addCarouselPreloading = (emblaApi) => {
  if (!emblaApi) return;

  log("🚀 Adding carousel preloading");

  const preloadedImages = new Set();

  const preloadNextImages = () => {
    const slides = emblaApi.slideNodes();
    const selectedIndex = emblaApi.selectedScrollSnap();

    // Preload next 2-3 images
    const indicesToPreload = [
      (selectedIndex + 1) % slides.length,
      (selectedIndex + 2) % slides.length,
      (selectedIndex + 3) % slides.length
    ];

    indicesToPreload.forEach(index => {
      const slide = slides[index];
      if (!slide) return;

      const img = slide.querySelector('img[data-src]');
      if (!img || preloadedImages.has(index)) return;

      const fullImageUrl = img.getAttribute('data-src');
      if (!fullImageUrl) return;

      // Create a hidden image element to trigger preloading
      const preloadImg = new Image();
      preloadImg.onload = () => {
        log(`✅ Preloaded image ${index + 1}`);
        preloadedImages.add(index);

        // Force lazysizes to load this image immediately when it comes into view
        if (img.getAttribute('data-src')) {
          img.setAttribute('data-preloaded', 'true');
        }
      };

      preloadImg.onerror = () => {
        log(`❌ Failed to preload image ${index + 1}`);
      };

      preloadImg.src = fullImageUrl;
      log(`⏳ Preloading image ${index + 1}: ${fullImageUrl.substring(0, 50)}...`);
    });
  };

  // Preload initially
  setTimeout(preloadNextImages, 500);

  // Preload when slide changes
  emblaApi.on('select', () => {
    setTimeout(preloadNextImages, 100);
  });

  // Also listen for when current image finishes loading
  const currentSlide = emblaApi.slideNodes()[emblaApi.selectedScrollSnap()];
  if (currentSlide) {
    const currentImg = currentSlide.querySelector('img');
    if (currentImg && currentImg.complete) {
      preloadNextImages();
    } else if (currentImg) {
      currentImg.addEventListener('load', preloadNextImages, { once: true });
    }
  }
};

const initEmblaMain = () => {
  log("🔄 Initializing main carousel");

  const elements = document.querySelectorAll(".embla--main");

  elements.forEach((el) => {
    const instance = EmblaCarousel(
      el,
      {
        align: "start",
        loop: true,
      },
      [Autoplay({ delay: 7000, stopOnInteraction: false })],
    );

    if (!instance || typeof instance.on !== "function") return;

    addKeyboardNavigation(instance, "main");

    // Attach next/prev button handlers
    const nextButton = el.parentElement?.querySelector(".embla__next");
    const prevButton = el.parentElement?.querySelector(".embla__prev");

    if (nextButton) {
      nextButton.addEventListener("click", () => {
        log("➡️ Next button clicked");
        instance.scrollNext();
      });
    }
    if (prevButton) {
      prevButton.addEventListener("click", () => {
        log("⬅️ Prev button clicked");
        instance.scrollPrev();
      });
    }

    const nav = document.querySelector("nav");
    const hasSimpleParent = el.closest(".carousel--simple");

    const updateNavClass = () => {
      if (hasSimpleParent) return;

      const index = instance.selectedScrollSnap();
      const slide = instance.slideNodes()[index];
      const isLight = slide?.getAttribute("data-light") === "true";

      nav?.classList.toggle("nav--light", isLight);
      log("Active slide:", slide);
    };

    instance.on("select", updateNavClass);
    updateNavClass();

    // Single vs double click handler
    instance.slideNodes().forEach((slide, index) => {
      let startX = 0;
      let startY = 0;
      let isDragging = false;
      let clickTimeout = null;

      slide.addEventListener("mousedown", (e) => {
        startX = e.clientX;
        startY = e.clientY;
        isDragging = false;
      });

      slide.addEventListener("mousemove", (e) => {
        const deltaX = Math.abs(e.clientX - startX);
        const deltaY = Math.abs(e.clientY - startY);
        if (deltaX > 5 || deltaY > 5) {
          isDragging = true;
        }
      });

      slide.addEventListener("click", (e) => {
        // If a link was clicked, don't trigger scroll
        if (e.target.closest("a")) {
          log(`🔗 Clicked a link inside slide ${index}, skipping scroll`);
          return;
        }

        if (isDragging) {
          isDragging = false;
          return;
        }

        if (clickTimeout) {
          clearTimeout(clickTimeout);
          clickTimeout = null;

          const link = slide.querySelector("a");
          const projectUrl = slide.getAttribute("data-project-url");
          if (link) {
            log(`🧭 Double click on slide ${index}, following link`);
            link.click();
          } else if (projectUrl) {
            log(`🧭 Double click on slide ${index}, navigating to project`);
            window.location.href = projectUrl;
          } else {
            log(`❗ Double click on slide ${index}, no link found`);
          }
          return;
        }

        clickTimeout = setTimeout(() => {
          clickTimeout = null;

          const link = slide.querySelector("a");
          const projectUrl = slide.getAttribute("data-project-url");
          log(`🖱️ Single click on slide ${index}`);
          if (link || projectUrl) {
            log(`🔗 Link found, single click = advance only`);
            instance.scrollNext();
          } else {
            log(`➡️ No link, advancing to next`);
            instance.scrollNext();
          }
        }, 250);
      });
    });

    emblaMain = instance;

    // Add intelligent preloading for smoother carousel experience
    addCarouselPreloading(instance);
  });
};

const initEmblaGallery = () => {
  log("🖼️ Initializing gallery carousels");

  const galleryEls = document.querySelectorAll(".embla--gallery");

  galleryEls.forEach((carouselEl) => {
    const [emblaInstance] = embla(
      carouselEl,
      [Fade(), Autoplay({ delay: 5000, stopOnInteraction: false })],
      { loop: true },
    ).filter((inst) => inst && typeof inst.on === "function");

    if (emblaInstance) {
      // Add keyboard navigation for gallery
      addKeyboardNavigation(emblaInstance, "gallery");
      // Add preloading for smoother transitions
      addCarouselPreloading(emblaInstance);
      log("Gallery carousel ready:", carouselEl);
    }
  });
};

const initEmblaOverlay = () => {
  log("🔄 Initializing overlay carousel");

  const emblaNode = document.querySelector(".embla--overlay");
  if (emblaNode && typeof EmblaCarousel === "function") {
    emblaOverlay = EmblaCarousel(
      emblaNode,
      {
        loop: true,
        drag: true,
      },
      [Fade()],
    );

    // Add keyboard navigation for overlay
    addKeyboardNavigation(emblaOverlay, "overlay");
  }

  const nextButton = document.querySelector(".embla__next");
  const prevButton = document.querySelector(".embla__prev");

  if (nextButton) {
    nextButton.addEventListener("click", () => {
      log("➡️ Next slide");
      emblaOverlay?.scrollNext();
    });
  }

  if (prevButton) {
    prevButton.addEventListener("click", () => {
      log("⬅️ Previous slide");
      emblaOverlay?.scrollPrev();
    });
  }
};

const initEmblaAutoWidth = () => {
  const [emblaInstance] = embla(".embla--main.embla--aw", [], {
    dragFree: false,
    containScroll: "keepSnaps",
    align: "start",
  }).filter((inst) => inst && typeof inst.on === "function");

  if (emblaInstance) {
    // Add keyboard navigation
    addKeyboardNavigation(emblaInstance, "auto-width");

    const updateNavClass = () => {
      const index = emblaInstance.selectedScrollSnap();
      const slide = emblaInstance.slideNodes()[index];
      const isLight = slide.getAttribute("data-light") === "true";

      document.querySelector("nav")?.classList.toggle("nav--light", isLight);
    };

    emblaInstance.on("select", updateNavClass);
    updateNavClass();
  }
};

const initEmblaProcess = () => {
  const processCarousel = document.querySelector(".embla--process");
  if (!processCarousel) return;

  const images = processCarousel.querySelectorAll("img");

  const imagePromises = Array.from(images).map(
    (img) =>
      new Promise((resolve) => {
        if (img.complete) {
          resolve();
        } else {
          img.onload = resolve;
          img.onerror = resolve;
        }
      }),
  );

  Promise.all(imagePromises).then(() => {
    log("✅ All process slider images loaded");

    const wheelPlugin = WheelGesturesPlugin({
      forceWheelAxis: "x",
    });

    emblaProcesses = embla(".embla--process", [wheelPlugin], {
      loop: false,
      align: "start",
      dragFree: true,
    }).filter((inst) => inst && typeof inst.on === "function");

    emblaProcesses.forEach((emblaApi) => {
      emblaApi.reInit();

      // Add keyboard navigation for process carousel
      addKeyboardNavigation(emblaApi, "process");

      checkSlideClick(emblaApi, openOverlay);

      const slides = emblaApi.slideNodes();
      slides.forEach((slide) => {
        slide.style.opacity = "1";
      });
    });
  });
};

const checkSlideClick = (emblaApi, openOverlay = null) => {
  const slides = emblaApi.slideNodes();

  slides.forEach((slide, index) => {
    let lastClick = 0;

    slide.addEventListener("click", (e) => {
      const now = Date.now();
      const isDoubleClick = now - lastClick < 300;
      lastClick = now;

      const link = slide.querySelector("a");

      if (isDoubleClick) {
        if (link) {
          log(`🧭 Double click: following link on slide ${index}`);
          link.click();
        } else {
          log(`⚠️ Double click: no link on slide ${index}`);
        }
        return;
      }

      // Single click
      if (typeof openOverlay === "function") {
        log(`🖼️ Single click: open overlay on slide ${index}`);
        openOverlay(slide);
      } else {
        log(`➡️ Single click: scroll next from slide ${index}`);
        emblaApi.scrollNext();
      }
    });
  });
};

// ==========================================================================
// Overlay
// ==========================================================================

const openOverlay = (slide) => {
  const overlay = document.getElementById("overlay");
  if (!overlay) return;

  overlayActive = true; // ✅ Disable nav scroll logic

  const emblaContainer = overlay.querySelector(".embla__container");
  if (!emblaContainer) return;

  const slides = Array.from(slide.parentElement.children);
  const clickedIndex = slides.indexOf(slide);

  const reorderedSlides = [
    slides[clickedIndex],
    ...slides.slice(0, clickedIndex),
    ...slides.slice(clickedIndex + 1),
  ];

  emblaContainer.innerHTML = reorderedSlides
    .map((slide) => {
      const imgElement = slide.querySelector("img");
      const imageSrc = imgElement?.getAttribute("data-img") || imgElement?.src;
      const imageCaption = imgElement?.getAttribute("data-caption");
      return `<div class="embla__slide">
                <div class="embla__inner">
                  <img src="${imageSrc}" alt="">
                  <p>${imageCaption}</p>
                </div>
              </div>`;
    })
    .join("");

  // Initialize Embla carousel for the overlay
  const emblaNode = overlay.querySelector(".embla--overlay");
  if (emblaNode && typeof EmblaCarousel === "function") {
    // Destroy existing instance if any
    if (emblaOverlay) {
      emblaOverlay.destroy();
    }

    emblaOverlay = EmblaCarousel(
      emblaNode,
      {
        loop: true,
        drag: true,
      },
      [Fade()],
    );

    // Add keyboard navigation for overlay
    addKeyboardNavigation(emblaOverlay, "overlay");

    // Attach button handlers
    const nextButton = overlay.querySelector(".embla__next");
    const prevButton = overlay.querySelector(".embla__prev");

    if (nextButton) {
      nextButton.addEventListener("click", () => {
        log("➡️ Overlay next slide");
        emblaOverlay?.scrollNext();
      });
    }

    if (prevButton) {
      prevButton.addEventListener("click", () => {
        log("⬅️ Overlay previous slide");
        emblaOverlay?.scrollPrev();
      });
    }
  }

  overlay.classList.add("active");

  const scrollY = window.scrollY;
  document.body.style.position = "fixed";
  document.body.style.top = `-${scrollY}px`;
  document.body.classList.add("fixed");

  const closeOverlay = () => {
    overlay.classList.remove("active");
    emblaContainer.innerHTML = "";

    document.body.style.position = "";
    document.body.style.top = "";
    document.body.classList.remove("fixed");
    window.scrollTo(0, scrollY);

    // ✅ Wait for user to scroll before re-enabling nav logic
    const onScroll = () => {
      overlayActive = false; // Reactivate only after user scrolls
      window.removeEventListener("scroll", onScroll); // ✅ Clean up
    };

    window.addEventListener("scroll", onScroll, { once: true }); // ✅ Only listen once
  };

  overlay
    .querySelector(".overlay__close")
    .addEventListener("click", closeOverlay);

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeOverlay();
  });

  overlay.addEventListener("click", (e) => {
    if (
      !e.target.closest(".embla__inner") &&
      !e.target.closest(".embla__prev") &&
      !e.target.closest(".embla__next")
    ) {
      closeOverlay();
    }
  });
};

// ==========================================================================
// Load More for Selected Work
// ==========================================================================
const loadMore = () => {
  log("🔄 Load more initialized");

  const list = document.querySelector(".projects__list");
  const btn = document.querySelector(".load-more-button");
  if (!list || !btn) return;

  const currentTag = list.getAttribute("data-tag") || "";
  const currentCategory = list.getAttribute("data-category") || "";

  const currentPath = window.location.pathname;

  const isArchivePage = currentPath.includes("/archive");
  const isNewsPage = currentPath.includes("/news");
  const isSelectedWorksPage = currentPath.includes("/selected-work");

  const baseUrl = window.location.origin;
  let endpoint = "selected-work.json";

  if (isArchivePage) {
    endpoint = "archive.json";
  } else if (isNewsPage) {
    endpoint = "news.json";
  }

  btn.addEventListener("click", async () => {
    // 🔥 Get the CURRENT page on each click
    let page = parseInt(list.getAttribute("data-page")) || 1;

    btn.textContent = "Loading...";
    btn.disabled = true;

    try {
      let url = `${baseUrl}/${endpoint}?page=${page}`;

      if (currentTag) url += `&tag=${currentTag}`;
      if (currentCategory) url += `&category=${currentCategory}`;

      log("🔄 Loading more from:", url);

      const res = await fetch(url);
      const data = await res.json();

      if (data.html) {
        list.insertAdjacentHTML("beforeend", data.html);

        setTimeout(() => {
          fadeUp();
          if (isArchivePage) scaleControl();
        }, 50);
      }

      if (window.lazyload) window.lazyload.update();

      if (data.more) {
        const nextPage = page + 1;
        list.setAttribute("data-page", nextPage); // 🧠 update DOM
        btn.textContent = "Load More";
        btn.disabled = false;
      } else {
        btn.style.display = "none";
      }
    } catch (error) {
      log("❌ Load error:", error);
      btn.textContent = "Try again";
      btn.disabled = false;
    }
  });
};

// ==========================================================================
// Filters for Selected Work
// ==========================================================================
const initFilters = () => {
  log("🎛️ Filters initialized");

  const projectList = document.querySelector(".projects__list");
  const loadMoreButton = document.querySelector(".load-more-button");

  document.querySelectorAll(".filter-link").forEach((link) => {
    link.addEventListener("click", async (event) => {
      event.preventDefault();

      projectList.classList.add("loading");

      const tag =
        link.dataset.tag || new URL(link.href).searchParams.get("tag") || "";
      const category =
        link.dataset.category ||
        new URL(link.href).searchParams.get("category") ||
        "";

      let path = window.location.pathname;

      let base = "archive.json";

      if (path.includes("/news")) {
        base = "news.json";
      } else if (path.includes("/selected-work")) {
        base = "selected-work.json";
      }

      let url = `${window.location.origin}/${base}`;

      const params = new URLSearchParams();

      if (tag) params.set("tag", tag);
      if (category) params.set("category", category);

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      log("🔍 Fetching:", url);

      const response = await fetch(url);
      const data = await response.json();

      log(`📊 Projects found: ${data.total}`);

      setTimeout(() => {
        projectList.innerHTML = data.html;
        projectList.classList.remove("loading");

        history.pushState({}, "", link.href);
        projectList.setAttribute("data-tag", tag);
        projectList.setAttribute("data-category", category);
        projectList.setAttribute("data-page", 2);
        scaleControl();
        fadeUp();

        if (loadMoreButton) {
          loadMoreButton.style.display = data.more ? "block" : "none";
          loadMoreButton.textContent = data.more
            ? "Load More"
            : "No more projects";
          loadMoreButton.disabled = !data.more;
        }
      }, 500);
    });
  });
};

// ==========================================================================
// Mobile side scroll, draggable Filter
// ==========================================================================

const initFilterScroll = () => {
  const nav = document.querySelector(".projects__filter");
  if (!isMobile) return;
  if (!nav) return;

  let isDown = false;
  let startX;
  let scrollLeft;
  log("✅ Mobile draggable Filter");

  // Mouse events
  nav.addEventListener("mousedown", (e) => {
    isDown = true;
    nav.classList.add("dragging");
    startX = e.pageX - nav.offsetLeft;
    scrollLeft = nav.scrollLeft;
    log("✅ Mobile draggable Filter - mouse down");
  });

  nav.addEventListener("mouseleave", () => {
    isDown = false;
    nav.classList.remove("dragging");
  });

  nav.addEventListener("mouseup", () => {
    isDown = false;
    nav.classList.remove("dragging");
  });

  nav.addEventListener("mousemove", (e) => {
    if (!isDown) return;
    e.preventDefault();
    const x = e.pageX - nav.offsetLeft;
    const walk = (x - startX) * 1.5;
    nav.scrollLeft = scrollLeft - walk;
  });

  // Touch events
  let touchStartX = 0;
  let touchScrollLeft = 0;

  nav.addEventListener("touchstart", (e) => {
    touchStartX = e.touches[0].pageX;
    touchScrollLeft = nav.scrollLeft;
  });

  nav.addEventListener("touchmove", (e) => {
    const x = e.touches[0].pageX;
    const walk = (x - touchStartX) * 1.5;
    nav.scrollLeft = touchScrollLeft - walk;
  });
};

// ==========================================================================
// Toggle Active Filter Link
// ==========================================================================

const toggleActiveLink = () => {
  const filterLinks = document.querySelectorAll(".filter-link");
  const currentPath = window.location.pathname + window.location.search;
  const allButton = document.querySelector(".filter-all");

  let matchFound = false;

  filterLinks.forEach((link) => link.classList.remove("active"));

  filterLinks.forEach((link) => {
    const linkPath =
      new URL(link.href, window.location.origin).pathname +
      new URL(link.href, window.location.origin).search;

    if (currentPath === linkPath) {
      link.classList.add("active");
      matchFound = true;
    }
  });

  if (!matchFound && currentPath.startsWith("/selected-work")) {
    allButton?.classList.add("active");
  }

  filterLinks.forEach((link) => {
    link.addEventListener("click", (event) => {
      filterLinks.forEach((link) => link.classList.remove("active"));
      event.currentTarget.classList.add("active");
    });
  });
};

// ==========================================================================
// Show all projects filter
// ==========================================================================

const initAllProjects = () => {
  const allProjects = document.querySelector(".projects__all");
  const projectList = document.querySelector(".projects__list");
  const loadMoreButton = document.querySelector(".load-more-button");

  if (!allProjects || !projectList) return;

  log("✅ Found All Projects link");

  allProjects.addEventListener("click", async (e) => {
    e.preventDefault();
    log("🔄 Resetting to All Projects...");

    if (projectList.classList.contains("loading")) return;

    projectList.classList.add("loading");

    const response = await fetch(
      `${window.location.origin}/selected-work.json`,
    );
    const data = await response.json();

    setTimeout(() => {
      projectList.innerHTML = data.html;
      projectList.classList.remove("loading");
      fadeUp();

      if (loadMoreButton) {
        loadMoreButton.style.display = data.more ? "block" : "none";
        loadMoreButton.textContent = data.more
          ? "Load More"
          : "No more projects";
        loadMoreButton.disabled = !data.more;
      }
    }, 500);
  });
};

// ==========================================================================
// All Nav Link
// ==========================================================================

const initNavLinks = () => {
  const navLinks = document.querySelectorAll(".nav__link a");

  if (!navLinks.length) return;

  navLinks.forEach((link) => {
    link.addEventListener("click", (e) => {
      const currentPath = window.location.pathname;
      const linkPath = new URL(link.href).pathname;

      if (currentPath === linkPath) {
        e.preventDefault();
        log("🔄 Reloading current page via Swup:", link.href);
        swup.navigate(link.href);
      }
    });
  });
};

// ==========================================================================
// Back Button Navigation
// ==========================================================================
const handleBackButton = () => {
  window.addEventListener("popstate", async () => {
    log("🔙 Back button pressed");

    const projectList = document.querySelector(".projects__list");
    const loadMoreButton = document.querySelector(".load-more-button");

    if (!projectList) return;

    const currentPath = window.location.pathname;
    const isArchivePage = currentPath.includes("/archive");

    const urlParams = new URLSearchParams(window.location.search);
    const tag = urlParams.get("tag") || "";
    const category = urlParams.get("category") || "";

    // Use the appropriate endpoint based on current page
    const url = isArchivePage
      ? `${window.location.origin}/archive.json`
      : `${window.location.origin}/selected-work.json`;

    // Add any query parameters
    const queryString = [];
    if (tag) queryString.push(`tag=${tag}`);
    if (category) queryString.push(`category=${category}`);

    const fullUrl =
      queryString.length > 0 ? `${url}?${queryString.join("&")}` : url;

    log("🔄 Fetching for back navigation:", fullUrl);

    projectList.classList.add("loading");

    try {
      const response = await fetch(fullUrl);
      const data = await response.json();

      log(`📊 Projects found after back navigation: ${data.total}`);

      setTimeout(() => {
        // ✅ Clear old content and insert new HTML
        projectList.innerHTML = data.html;
        projectList.classList.remove("loading");

        // ✅ Update attributes
        projectList.setAttribute("data-tag", tag);
        projectList.setAttribute("data-category", category);
        projectList.setAttribute("data-page", 2);

        // ✅ Call fadeUp to re-trigger GSAP animations
        // fadeUp();

        // ✅ Handle load more button state
        if (loadMoreButton) {
          loadMoreButton.style.display = data.more ? "block" : "none";
          loadMoreButton.textContent = data.more
            ? "Load More"
            : "No more projects";
          loadMoreButton.disabled = !data.more;
        }
      }, 300);
    } catch (error) {
      log("❌ Error fetching projects:", error);
      projectList.classList.remove("loading");
    }
  });
};

// ==========================================================================
// Nav Toggle
// ==========================================================================
let lastScrollY = window.scrollY;
let overlayActive = false;

const handleScroll = () => {
  if (overlayActive) return;

  if (window.matchMedia("(min-width: 769px)").matches) {
    const currentY = window.scrollY;

    if (currentY <= 0) {
      // We're at the top — remove scrolled class
      nav.classList.remove("nav--scrolled");
    } else if (currentY > lastScrollY) {
      // Scrolling down
      nav.classList.add("nav--scrolled");
    } else if (currentY < lastScrollY) {
      // Scrolling up
      nav.classList.remove("nav--scrolled");
    }

    lastScrollY = currentY;
  }
};

window.addEventListener("scroll", handleScroll);

// ==========================================================================
// Handle Image Scaler Image resolution
// ==========================================================================

const handleImageRes = () => {
  const items = document.querySelectorAll(
    ".projects__list .projects__item img",
  );
  const widthThreshold = 1140;

  if (typeof handleImageRes.currentObserver !== "undefined") {
    handleImageRes.currentObserver.disconnect();
  }

  if (typeof handleImageRes.resizeHandler !== "undefined") {
    window.removeEventListener("resize", handleImageRes.resizeHandler);
  }

  if (!items.length) return;

  const updateImageVersion = () => {
    items.forEach((img) => {
      const largeImageUrl = img.getAttribute("data-large");
      if (!largeImageUrl) return;

      const currentWidth = img.clientWidth;
      const originalSrc = img.getAttribute("data-src");

      if (currentWidth > widthThreshold) {
        // Only update if necessary and if we're already loaded
        if (img.classList.contains("lazyloaded") && img.src !== largeImageUrl) {
          // Store the original if we haven't yet
          if (!img.hasAttribute("data-original-src")) {
            img.setAttribute("data-original-src", img.src);
            img.setAttribute("data-original-data-src", originalSrc);
          }

          // Set to high-res version without touching lazy loading
          img.src = largeImageUrl;
        }
      } else if (img.hasAttribute("data-original-src")) {
        // Restore the original source
        img.src = img.getAttribute("data-original-src");
      }
    });
  };

  const observer = new ResizeObserver(() => {
    updateImageVersion();
  });

  items.forEach((img) => {
    observer.observe(img);
  });

  handleImageRes.currentObserver = observer;

  const handleResize = () => updateImageVersion();
  window.addEventListener("resize", handleResize);
  handleImageRes.resizeHandler = handleResize;

  // Wait a short moment to make sure lazy loading is complete
  setTimeout(updateImageVersion, 100);
};

// ==========================================================================
// Overlay Cursor
// ==========================================================================

// function initNextPrevCursor() {
//   const cursor = document.createElement('div');
//   cursor.classList.add('custom-cursor');
//   cursor.style.backgroundImage = `url('../images/arrow.svg')`;
//   document.body.appendChild(cursor);

//   const moveCursor = (e) => {
//     cursor.style.left = `${e.clientX}px`;
//     cursor.style.top = `${e.clientY}px`;
//   };

//   document.querySelectorAll('.embla__prev, .embla__next').forEach((button) => {
//     button.addEventListener('mouseenter', (e) => {
//       cursor.style.opacity = '1';
//       cursor.style.transform = `rotate(${e.target.dataset.rotate || 0}deg)`;
//       document.addEventListener('mousemove', moveCursor);
//     });

//     button.addEventListener('mouseleave', () => {
//       cursor.style.opacity = '0';
//       document.removeEventListener('mousemove', moveCursor);
//     });
//   });
// }

// ==========================================================================
// Change and to &
// ==========================================================================

function replaceAndWithAmpersand(selector = ".projects__filter") {
  document.querySelectorAll(selector).forEach((el) => {
    el.innerHTML = el.innerHTML.replace(/\band\b/g, "&");
  });
}

// ==========================================================================
// Add nav colour change based on project template

function toggleNavColor() {
  const container = document.querySelector('[data-nav="dark"]');
  const nav = document.querySelector("nav");

  if (container && nav) {
    nav.classList.add("nav--light");
  }
}

// ==========================================================================
// Team overlay
// ==========================================================================
function openTeamOverlay(options = {}) {
  const overlay = document.getElementById(options.overlayId || "teamOverlay");
  const triggerSelector = options.triggerSelector || ".team__toggle";
  const scrollLockClass = options.scrollLockClass || "fixed";

  if (!overlay) return;

  let scrollY = 0;

  const open = (button) => {
    const member = button.closest(".team__member");
    if (!member) return;

    const imageSrc = member.getAttribute("data-img");
    const name = member.getAttribute("data-name");
    const position = member.getAttribute("data-position");
    const aboutText = member.getAttribute("data-about");

    overlay.querySelector("#teamOverlayImage").src = imageSrc;
    overlay.querySelector("#teamOverlayName").textContent = name;
    overlay.querySelector("#teamOverlayPosition").textContent = position;
    overlay.querySelector("#teamOverlayText").innerHTML = aboutText;

    scrollY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.classList.add(scrollLockClass);

    overlay.classList.add("active");

    // ✅ Slight delay so the nav doesn’t glitch visually
    setTimeout(() => {
      document.querySelector("nav")?.classList.add("nav--scrolled");
    }, 50);
  };

  const close = () => {
    overlay.classList.remove("active");
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.classList.remove(scrollLockClass);
    window.scrollTo(0, scrollY);

    // ✅ Remove nav--scrolled if it was forced
    // document.querySelector('nav')?.classList.remove('nav--scrolled');

    const onScroll = () => {
      window.removeEventListener("scroll", onScroll);
    };
    window.addEventListener("scroll", onScroll, { once: true });
  };

  // Attach listeners
  document.querySelectorAll(triggerSelector).forEach((button) => {
    button.addEventListener("click", () => open(button));
  });

  overlay
    .querySelector(".team-overlay__close")
    ?.addEventListener("click", close);

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") close();
  });

  overlay.addEventListener("click", (e) => {
    if (!e.target.closest(".team-overlay__inner")) close();
  });
}

// ==========================================================================
// Add search
// ==========================================================================
// Related tags - Fixed version
function renderRelatedKeywords(results) {
  const keywordSet = new Set();
  const input = document.getElementById("live-search-input");
  const query = input?.value?.trim().toLowerCase();

  // Check if current query is a partner name by seeing if all results have the same partner
  const isPartnerQuery = checkIfPartnerQuery(results, query);

  results.forEach((item) => {
    // Always include tags and categories (but filter duplicates of the query)
    const fields = ["categories", "tags"];
    fields.forEach((field) => {
      if (Array.isArray(item[field])) {
        item[field].forEach((entry) => {
          const clean = entry?.trim();
          if (clean && clean.toLowerCase() !== query) {
            keywordSet.add(clean);
          }
        });
      }
    });

    // For partners: be more strict about when to include them
    if (Array.isArray(item.partners) && !isPartnerQuery) {
      item.partners.forEach((partner) => {
        const clean = partner?.trim();
        const cleanLower = clean?.toLowerCase();
        if (
          clean &&
          cleanLower !== query && // Exact match exclusion
          query.length >= 3 && // ignore too-short fuzzy terms
          cleanLower.includes(query) &&
          !query.includes(cleanLower) // Don't show if query already contains the full partner name
        ) {
          keywordSet.add(clean);
        }
      });
    }
  });

  const keywords = [...keywordSet].sort();
  const container = document.getElementById("related-tags");
  if (!container) return;

  if (keywords.length) {
    const tagsHTML = keywords
      .map(
        (keyword) =>
          `<a href="#" class="search__tag tag" data-keyword="${keyword}">${keyword}</a>`,
      )
      .join(", ");

    container.innerHTML = `
      <div class="search__tags">
        ${tagsHTML}
      </div>
    `;

    setupTagClickListeners();
  } else {
    container.innerHTML = "";
  }
}

// Helper function to determine if the current query is a partner query
function checkIfPartnerQuery(results, query) {
  if (!results.length || !query) return false;

  const queryLower = query.toLowerCase().trim();

  // Get all unique partners from the results
  const allPartnersInResults = new Set();
  results.forEach((item) => {
    if (Array.isArray(item.partners)) {
      item.partners.forEach((partner) => {
        if (partner?.trim()) {
          allPartnersInResults.add(partner.trim().toLowerCase());
        }
      });
    }
  });

  // Check if the query exactly matches any partner name or if all results share the same partner
  // that matches the query
  const exactMatch = [...allPartnersInResults].some(
    (partner) => partner === queryLower,
  );

  // If exact match, it's definitely a partner query
  if (exactMatch) return true;

  // If there's only one unique partner and the query is contained within it (partial match)
  // or the partner is contained within the query, it's likely a partner query
  if (allPartnersInResults.size === 1) {
    const singlePartner = [...allPartnersInResults][0];
    // Check if query is a significant portion of the partner name (at least 3 chars and 50% match)
    if (
      queryLower.length >= 3 &&
      (singlePartner.includes(queryLower) || queryLower.includes(singlePartner))
    ) {
      return true;
    }
  }

  return false;
}

function setupTagClickListeners() {
  document.querySelectorAll(".search__tag").forEach((link) => {
    link.addEventListener("click", (e) => {
      e.preventDefault(); // stop anchor from jumping
      const keyword = link.getAttribute("data-keyword");
      const input = document.getElementById("live-search-input");
      if (!input) return;
      input.value = keyword;
      input.dispatchEvent(new Event("keyup"));
    });
  });
}

function initLiveSearch() {
  const input = document.getElementById("live-search-input");
  const clearBtn = document.querySelector(".clear-btn");
  const resultsList = document.getElementById("live-search-results");
  const relatedTags = document.getElementById("related-tags");

  if (!input || !resultsList) return;

  let debounceTimer;

  const performSearch = () => {
    const query = input.value.trim();

    if (query.length < 2) {
      resultsList.innerHTML = "";
      if (relatedTags) relatedTags.innerHTML = "";
      if (clearBtn) clearBtn.style.display = "none";
      return;
    }

    fetch(`/search.json?q=${encodeURIComponent(query)}`)
      .then((res) => res.json())
      .then((results) => {
        const grouped = {
          selected: [],
          archive: [],
          news: [],
        };

        results.forEach((result) => {
          if (grouped[result.type]) {
            grouped[result.type].push(result);
          }
        });

        const labels = {
          selected: "Selected Work",
          archive: "Archive",
          news: "News",
        };

        let html = "";
        let visibleResults = [];

        Object.entries(grouped).forEach(([type, items]) => {
          if (items.length === 0) return;

          visibleResults = visibleResults.concat(items);

          html += `<div class="search__group search__group--${type}">`;
          html += `<div class="search__title"><h3>${labels[type]}</h3></div><ul class="search__listing">`;

          html += items
            .map((item) => {
              const link =
                item.type === "news" && item.link ? item.link : item.url;
              const isExternal = item.type === "news" && item.link;

              return `
              <li class="search-item">
                <a href="${link}" ${isExternal ? 'target="_blank" rel="noopener noreferrer"' : ""}>
                  ${item.thumb ? `<img src="${item.thumb}" alt="${item.title}" />` : ""}
                  <span class="item-meta">
                    <div class="title">${item.title}</div>
                    ${item.tags?.length ? `<div class="tags">${item.tags.join(", ")}</div>` : ""}
                    ${item.categories?.length ? `<div class="categories">${item.categories.join(", ")}</div>` : ""}
                  </span>
                </a>
              </li>
            `;
            })
            .join("");

          html += `</ul></div>`;
        });

        if (visibleResults.length === 0) {
          resultsList.innerHTML = `<div class="search__no-results">No results found.</div>`;
          if (relatedTags) relatedTags.innerHTML = "";
          return;
        }

        resultsList.innerHTML = html;
        renderRelatedKeywords(visibleResults);
        if (clearBtn) clearBtn.style.display = "block";
      });
  };

  // Search on Enter
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      performSearch();
    }
  });

  // Optional: Also debounce live typing
  input.addEventListener("keyup", () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      performSearch();
    }, 200);
  });

  // Handle ?q= query in URL
  const urlParams = new URLSearchParams(window.location.search);
  const initialQuery = urlParams.get("q");
  if (initialQuery && initialQuery.length >= 2) {
    input.value = initialQuery;
    if (clearBtn) clearBtn.style.display = "block";
    performSearch();
  }
}

// Inite Clear Search button
function initClearSearchButton() {
  const input = document.getElementById("live-search-input");
  const clearBtn = document.querySelector(".clear-btn");

  if (!input || !clearBtn) return;

  clearBtn.addEventListener("click", () => {
    input.value = "";
    input.focus();
    clearBtn.style.display = "none";

    const resultsList = document.getElementById("live-search-results");
    if (resultsList) resultsList.innerHTML = "";

    const relatedTags = document.getElementById("related-tags");
    if (relatedTags) relatedTags.innerHTML = "";
  });

  input.addEventListener("input", () => {
    clearBtn.style.display = input.value.length > 0 ? "block" : "none";
  });

  // clearBtn.style.display = 'none';
}

// ==========================================================================
// Align image caption
// ==========================================================================

// function alignCarouselCaptions() {
//   document.querySelectorAll('.carousel--simple .embla__slide').forEach(slide => {
//     const img = slide.querySelector('img');
//     const caption = slide.querySelector('.carousel__credit');

//     if (!img || !caption) return;

//     if (!img.complete) {
//       img.addEventListener('load', () => alignCaptionToImage(slide, img, caption), { once: true });
//     } else {
//       alignCaptionToImage(slide, img, caption);
//     }
//   });
// }

// function alignCaptionToImage(slide, img, caption) {
//   const imageLeft = img.getBoundingClientRect().left;
//   const slideLeft = slide.getBoundingClientRect().left;
//   const offset = imageLeft - slideLeft;

//   caption.style.position = 'relative';
//   caption.style.left = `${offset}px`;
//   caption.style.marginTop = '0.5rem';
//   caption.style.textAlign = 'left';
// }

// window.addEventListener('resize', alignCarouselCaptions);

// // if (typeof embla !== 'undefined') {
// //   embla.on('select', alignCarouselCaptions);
// // }

// ==========================================================================
// Initialize Site
// ==========================================================================

// Initialize Swup 🚀
const swup = new Swup();

swup.hooks.on("visit:start", () => {
  log("🔄 Page transition started");
  document.documentElement.classList.add("loading");
  document.documentElement.classList.remove("loaded");
  return new Promise((resolve) => setTimeout(resolve, 250));
});

swup.hooks.on("page:view", () => {
  requestAnimationFrame(() => {
    log("✅ Page view loaded (delayed)");
    document.documentElement.classList.add("loaded");
    document.documentElement.classList.remove("loading");
    init();
    scaleControl();
  });
});

// Main Init Function 🏁
const init = () => {
  log("🏁 Initializing site");
  checkHomePage();
  lazySizes.init();
  initAnimations();
  removeBr("h1");
  loadMore();
  setActiveNav();
  initFilters();
  toggleActiveLink();
  initAllProjects();
  initNavLinks();
  handleBackButton();
  fadeUp();
  // staggerFade();
  toggleActiveLink();
  initEmblaMain();
  initEmblaProcess();
  initEmblaOverlay();
  initEmblaGallery();
  initEmblaAutoWidth();
  embla();
  // alignCarouselCaptions();
  // scaleControl();
  handleImageRes();
  // replaceAndWithAmpersand();
  toggleNavColor();
  // openTeamOverlay();
  initLiveSearch();
  initClearSearchButton();
  initFilterScroll();

  // 🧠 Delay scaleControl to wait for images to load
  const projectImages = document.querySelectorAll(
    ".projects__list .projects__item img",
  );
  if (projectImages.length) {
    Promise.all(
      Array.from(projectImages).map((img) => {
        return img.complete
          ? Promise.resolve()
          : new Promise((resolve) => {
              img.onload = resolve;
              img.onerror = resolve;
            });
      }),
    ).then(() => {
      log("📸 Initial project images loaded — running scaleControl");
      scaleControl();
    });
  } else {
    scaleControl(); // fallback
  }
};

document.addEventListener("DOMContentLoaded", init);
document.addEventListener("swup:contentReplaced", init);

// console.log('loaded');